import { useCallback, useState } from "react";
import { DownloadStateEnum } from "../models/download-state.enum";
import { downloadSite, synchronizeSite } from "../../service-worker/communication.service";
import { useOfflineSiteContext } from "../../offline/contexts/offline-site.context";
import { useSiteToaster } from "./use-site-toaster.hook";

export const useSiteOffline = (orgaCode: string, siteId: string) => {
  const { addOneOfflineSite, removeOneOfflineSite } = useOfflineSiteContext();
  const { showSuccessMessage, showErrorMessage } = useSiteToaster();

  const [downloadingState, toggleDownloadingState] = useState<DownloadStateEnum>(DownloadStateEnum.IDLE);
  const [synchronizationState, toggleSynchronizationState] = useState<boolean>(false);

  const launchDownload = useCallback(
    async (selectedBuildingIds: string[], downloadOutdoorAreas: boolean, successCallback: () => Promise<void>) => {
      try {
        toggleDownloadingState(DownloadStateEnum.IN_PROGRESS);
        await downloadSite(orgaCode, siteId, selectedBuildingIds, downloadOutdoorAreas);
        await showSuccessMessage();
        await successCallback();
        toggleDownloadingState(DownloadStateEnum.SUCCESS);
        addOneOfflineSite();
      } catch (e) {
        toggleDownloadingState(DownloadStateEnum.ERROR);
        await showErrorMessage();
      }
    },
    [orgaCode, siteId, showSuccessMessage, showErrorMessage, toggleDownloadingState, addOneOfflineSite]
  );
  const launchSync = useCallback(
    async (selectedBuildingIds: string[] | undefined, syncOutdoorAreas: boolean | undefined, successCallback: () => Promise<void>) => {
      toggleSynchronizationState(true);
      try {
        await synchronizeSite(orgaCode, siteId, selectedBuildingIds, syncOutdoorAreas);
        // Vérifier si tous les bâtiments et l'espace extérieur ont été synchronisés
        const allBuildingsSynced = (!selectedBuildingIds || selectedBuildingIds.length === 0) && (syncOutdoorAreas === undefined || syncOutdoorAreas);
        if (allBuildingsSynced) {
          removeOneOfflineSite();
        }
        await successCallback();
      } catch (e) {
        await showErrorMessage();
      }
      toggleSynchronizationState(false);
    },
    [toggleSynchronizationState, orgaCode, siteId, showErrorMessage, removeOneOfflineSite]
  );

  return {
    launchDownload,
    downloadingState,
    launchSync,
    synchronizationState,
  };
};
